import React, { useState, useMemo, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Star, MessageCircle, User, Sparkles, TrendingUp, Compass, Activity, Shield,
  ScrollText, Sun, Moon, Map, Flame, Eye, Award, Layers, BookOpen,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SmartOfferBanner } from "../components/PromoBanners";
import KundliPdfActions from "../components/KundliPdfActions";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { claimLocalReport, signupUrl } from "../lib/claimReport";
import FirstReadCard from "../components/FirstReadCard";
import NatureReadCard from "../components/NatureReadCard";
import LifeTimeline from "../components/LifeTimeline";
import InviteFriend from "../components/InviteFriend";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Title-keyword router — order matters (first match wins).
 * Each tab routes by REGEX against the section title, NOT the number.
 * This makes the dashboard robust to any future change in the upstream
 * RAG report formatting (new section, renumber, missing section, etc.).
 */
const TAB_ROUTES = [
  { id: "profile",     label: "Profile",     icon: User,       color: "#FFD166", test: /native\s*profile|profile|identity|personal\s*data/i },
  { id: "snapshot",    label: "Snapshot",    icon: Star,       color: "#fde68a", test: /quick\s*snapshot|snapshot|overview/i },
  { id: "rashi",       label: "Rashi (D1)",  icon: Sun,        color: "#fdba74", test: /\brashi\b|\bd-?1\b|lagna\s*chart|natal\s*chart/i },
  { id: "navamsa",     label: "Navamsa (D9)",icon: Moon,       color: "#a78bfa", test: /navamsa|\bd-?9\b/i },
  { id: "dashamsa",    label: "Dashamsa(D10)",icon: Award,     color: "#67e8f9", test: /dashamsa|\bd-?10\b|career\s*chart/i },
  { id: "transits",    label: "Transits",    icon: Map,        color: "#86efac", test: /transit|gochar|current\s*planetary/i },
  { id: "yogas",       label: "Yogas",       icon: Sparkles,   color: "#86efac", test: /\byogas?\b/i },
  // Doshas MUST come before Dashas in this list — both share characters but Doshas test is more specific
  { id: "doshas",      label: "Doshas",      icon: Shield,     color: "#fca5a5", test: /\bdoshas?\b|mangal|kaal\s*sarp|pitr|sade\s*sati/i },
  { id: "combust",     label: "Combust",     icon: Flame,      color: "#fb923c", test: /combust/i },
  { id: "dashas",      label: "Dashas",      icon: Compass,    color: "#fdba74", test: /\bdasha[s]?\b|vimsottari|antardasha|mahadasha|pratyantar/i },
  { id: "jaimini",     label: "Jaimini",     icon: ScrollText, color: "#f9a8d4", test: /jaimini|atmakaraka|karakas/i },
  { id: "predictions", label: "Predictions", icon: TrendingUp, color: "#67e8f9", test: /predictions?|forecast|future|key\s*events/i },
  { id: "summary",     label: "Summary",     icon: Eye,        color: "#FFD166", test: /final\s*summary|conclusion|summary|takeaway/i },
  { id: "remedies",    label: "Remedies",    icon: Flame,      color: "#fb923c", test: /remed|mantra|gemstone|donation|upay/i },
];

const FALLBACK_TAB = { id: "more", label: "More", icon: BookOpen, color: "#c084fc" };

/**
 * Drop these section titles entirely — they're internal raw-data dumps.
 * Iteration-11: keep RAG Blocks too (user requested EVERYTHING be visible).
 */
const HIDDEN_TITLE_PATTERNS = [];

function routeByTitle(title) {
  for (const r of TAB_ROUTES) {
    if (r.test.test(title)) return r;
  }
  return null;
}

function makeId(base, used) {
  let id = base;
  let n = 2;
  while (used.has(id)) {
    id = `${base}-${n++}`;
  }
  used.add(id);
  return id;
}

/**
 * Parse the report markdown into ORDERED, title-routed sections.
 *
 * PERMANENT-FIX strategy (iter-12):
 * Everything that comes BEFORE the first numbered "## N. Title" H2 is captured
 * as the **Report Dashboard** tab — regardless of whether the upstream Python
 * RAG API emits a "## Report Dashboard" header, an H1, plain prose, or
 * nothing at all. This makes the Dashboard tab present 100% of the time.
 */
function parseReportSections(report) {
  if (!report) return [];
  const lines = report.split("\n");
  const sections = [];
  let current = null;
  const usedIds = new Set();

  // ── Pre-scan: split the document at the FIRST numbered "## N. Title" line ──
  const numberedHeaderIdx = lines.findIndex(
    (ln) => /^##\s+\d+\.\s+.+$/.test(ln)
  );
  const dashboardLines = numberedHeaderIdx === -1 ? lines.slice(0) : lines.slice(0, numberedHeaderIdx);
  const restLines = numberedHeaderIdx === -1 ? [] : lines.slice(numberedHeaderIdx);

  // ── Build the always-present "Report Dashboard" tab from the preamble ──
  // Keep H2/H3 sub-headers intact so ReactMarkdown renders them properly.
  // Strip H1 lines (document title) — they look ugly inside a tab.
  let dashMd = dashboardLines
    .filter((ln) => !(ln.startsWith("# ") && !ln.startsWith("## ")))
    .join("\n")
    .replace(/^##\s+Report Dashboard\s*$/im, "")  // remove a redundant H2 if upstream included one
    .trim();

  // FAIL-SAFE: if there is no preamble whatsoever (upstream skipped the intro
  // and started straight at `## 1. ...`), synthesise a Report Dashboard from
  // the key summary fields so the tab is ALWAYS visible to the customer.
  if (!dashMd) {
    const sum = parseSummaryFromReport(report);
    const rows = [
      sum.ascendant && `- **Ascendant:** ${sum.ascendant}`,
      sum.sun_sign  && `- **Sun Sign:** ${sum.sun_sign}`,
      sum.moon_sign && `- **Moon Sign:** ${sum.moon_sign}`,
      sum.nakshatra && `- **Nakshatra:** ${sum.nakshatra}`,
      sum.mahadasha && `- **Current Mahadasha:** ${sum.mahadasha}`,
    ].filter(Boolean).join("\n");
    if (rows) {
      dashMd = "### Overview\n\n" + rows + "\n\nUse the tabs above to explore your full chart in detail.";
    } else {
      dashMd = "### Overview\n\nUse the tabs above to explore your full Kundli report.";
    }
  }

  sections.push({
    id: makeId("report-dashboard", usedIds),
    num: 0,
    label: "Report Dashboard",
    icon: Layers,
    color: "#c084fc",
    title: "Report Dashboard",
    markdown: dashMd,
  });

  // ── Now parse the remaining numbered sections as before ──
  const flush = () => {
    if (!current) return;
    const md = current.bufferLines.join("\n").trim();
    if (md) sections.push({ ...current, markdown: md });
    current = null;
  };

  for (const line of restLines) {
    if (line.startsWith("# ") && !line.startsWith("## ")) continue;
    if (line.startsWith("## ")) {
      flush();
      const raw = line.slice(3).trim();
      const m = raw.match(/^(\d+)\.\s*(.+)$/);
      const num = m ? parseInt(m[1], 10) : 999;
      const rawTitle = (m ? m[2] : raw)
        .replace(/\s*\(recomputed[^)]*\)/i, "")
        .replace(/\s*\(Data-based only\)/i, "")
        .trim();

      if (HIDDEN_TITLE_PATTERNS.some((p) => p.test(rawTitle))) {
        current = null;
        continue;
      }

      const route = routeByTitle(rawTitle);
      const finalLabel = route ? route.label : (rawTitle.length > 18 ? rawTitle.slice(0, 18) + "…" : rawTitle);
      const finalRoute = route || FALLBACK_TAB;
      current = {
        id: makeId(finalRoute.id, usedIds),
        num,
        label: finalLabel,
        icon: finalRoute.icon,
        color: finalRoute.color,
        title: rawTitle,
        bufferLines: [],
      };
      continue;
    }
    if (current) current.bufferLines.push(line);
  }
  flush();

  // Sort: Report Dashboard (num=0) first, then numbered ascending.
  sections.sort((a, b) => a.num - b.num);
  return sections;
}

function parseSummaryFromReport(report) {
  if (!report) return {};
  const pick = (re) => {
    const m = report.match(re);
    return m && m[1] ? m[1].trim() : "";
  };
  const cleanSign = (v) => (v || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
  return {
    ascendant: cleanSign(pick(/\*\*Lagna \(Ascendant\):\*\*\s*([^\n]+)/i) || pick(/\*\*Ascendant:\*\*\s*([^\n]+)/i)),
    moon_sign: cleanSign(pick(/\*\*Moon Sign:\*\*\s*([^\n]+)/i)),
    sun_sign:  cleanSign(pick(/\*\*Sun Sign:\*\*\s*([^\n]+)/i)),
    nakshatra: pick(/\*\*Nakshatra:\*\*\s*([^\n]+)/i).replace(/\s*\(\d+\)\s*/, " ").trim(),
    mahadasha: pick(/\*\*Current Mahadasha:\*\*\s*([^\n]+)/i) ||
               (report.match(/\b(Sun|Moon|Mars|Mercury|Jupiter|Venus|Saturn|Rahu|Ketu)\s+Mahadasha\b/i) || [])[1] || "",
  };
}

/* PDF export via window.print() — A4, branded, all sections included */
function downloadAsPdf({ name, birthLine, summary, sections }) {
  const safe = (s) => (s || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const mdToHtml = (md) => {
    if (!md) return "";
    let html = safe(md);
    const lines = html.split("\n");
    const out = [];
    let i = 0;
    while (i < lines.length) {
      const ln = lines[i];
      const headerMatch = ln.match(/^\|(.+)\|$/);
      const sepMatch = lines[i + 1] && lines[i + 1].match(/^\|\s*[-:]+[-:|\s]*\|$/);
      if (headerMatch && sepMatch) {
        const cells = headerMatch[1].split("|").map((s) => s.trim());
        let table = `<table><thead><tr>${cells.map((c) => `<th>${c}</th>`).join("")}</tr></thead><tbody>`;
        i += 2;
        while (i < lines.length && lines[i].startsWith("|")) {
          const rowCells = lines[i].replace(/^\||\|$/g, "").split("|").map((s) => s.trim());
          table += `<tr>${rowCells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
          i++;
        }
        table += "</tbody></table>";
        out.push(table);
        continue;
      }
      out.push(ln);
      i++;
    }
    html = out.join("\n")
      .replace(/^### (.+)$/gm, "<h4>$1</h4>")
      .replace(/^## (.+)$/gm, "<h3>$1</h3>")
      .replace(/^# (.+)$/gm, "<h2>$1</h2>")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/^- (.+)$/gm, "<li>$1</li>")
      .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
      .replace(/\n\n+/g, "</p><p>");
    return `<p>${html}</p>`;
  };
  const summaryHtml = `
    <div class="summary-grid">
      <div><span>Ascendant</span><b>${safe(summary.ascendant) || "—"}</b></div>
      <div><span>Sun Sign</span><b>${safe(summary.sun_sign) || "—"}</b></div>
      <div><span>Moon Sign</span><b>${safe(summary.moon_sign) || "—"}</b></div>
      <div><span>Nakshatra</span><b>${safe(summary.nakshatra) || "—"}</b></div>
      <div><span>Mahadasha</span><b>${safe(summary.mahadasha) || "—"}</b></div>
    </div>`;
  const sectionsHtml = sections.map((s) => `
    <section class="report-section" style="border-left:4px solid ${s.color};">
      <h3 style="color:${s.color};">${safe(s.title || s.label)}</h3>
      <div>${mdToHtml(s.markdown)}</div>
    </section>`).join("\n");
  const html = `<!doctype html><html><head><meta charset="utf-8"/>
    <title>AstroFate Kundli — ${safe(name)}</title>
    <style>
      @page { size: A4; margin: 14mm 14mm 18mm; }
      body { font-family: 'Georgia', serif; color:#222; max-width:760px; margin:0 auto; }
      h1 { color:#7c3aed; font-family:'Cinzel',serif; margin:0 0 4px; font-size:28px; }
      h3 { font-size:16px; margin:16px 0 6px; }
      h4 { font-size:14px; margin:10px 0 4px; color:#555; }
      .meta { color:#666; font-size:12px; margin-bottom:18px; }
      .summary-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin:14px 0 22px; }
      .summary-grid div { padding:10px 8px; border:1px solid #ddd; border-radius:8px; text-align:center; }
      .summary-grid span { display:block; font-size:10px; color:#888; text-transform:uppercase; letter-spacing:.5px; margin-bottom:3px; }
      .summary-grid b { font-size:13px; color:#7c3aed; }
      .report-section { margin:18px 0; padding:10px 14px; background:#faf9ff; border-radius:6px; page-break-inside: avoid; }
      table { border-collapse:collapse; width:100%; margin:8px 0; font-size:12px; }
      th, td { border:1px solid #ddd; padding:5px 8px; text-align:left; }
      th { background:#f0eaff; }
      p { line-height:1.55; font-size:13px; }
      ul { padding-left:20px; }
      li { font-size:13px; line-height:1.55; }
      .footer { margin-top:30px; padding-top:14px; border-top:1px solid #ddd; color:#888; font-size:11px; text-align:center; }
      @media print { .no-print { display:none; } }
    </style></head><body>
    <h1>AstroFate Kundli</h1>
    <div class="meta"><strong>${safe(name)}</strong> · ${safe(birthLine)}</div>
    ${summaryHtml}
    ${sectionsHtml}
    <div class="footer">Generated by AstroFate · theastrofate.com</div>
    </body></html>`;
  // SECURE PRINT FLOW (no document.write / no inline script eval).
  // 1. Strip any inline <script> tags from the constructed HTML (defence-in-
  //    depth — every user-controlled field is already passed through `safe()`,
  //    but we belt-and-suspenders the output before handing it to the browser).
  // 2. Serve the HTML as a Blob URL into a new window — the browser parses it
  //    as a normal HTTP document; no DOM injection on our origin.
  // 3. Trigger window.print() from THIS window once the child loads.
  const safeHtml = html.replace(/<script[\s\S]*?<\/script>/gi, "");
  const blob = new Blob([safeHtml], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=900,height=900");
  if (!w) {
    URL.revokeObjectURL(url);
    alert("Please allow pop-ups for this site to download your PDF.");
    return;
  }
  // Trigger print after the child window paints — runs in the child's context
  // without us touching its document. Falls back gracefully if blocked.
  const triggerPrint = () => {
    try { w.focus(); w.print(); } catch (_) { /* user can hit Ctrl+P */ }
  };
  w.addEventListener("load", () => setTimeout(triggerPrint, 350), { once: true });
  // Belt-and-suspenders: some browsers fire `load` before our listener attaches.
  setTimeout(() => { if (!w.closed) triggerPrint(); }, 1200);
  // Release the blob URL once the child closes.
  const release = () => { try { URL.revokeObjectURL(url); } catch (_) { /* already revoked */ } };
  w.addEventListener("beforeunload", release, { once: true });
  setTimeout(release, 60_000);
}

export default function KundliReportPage() {
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const [parsedReport, setParsedReport] = useState(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("astrofate_report") : null;
    if (!stored) return null;
    try { return JSON.parse(stored); } catch { return null; }
  });
  const [fetching, setFetching] = useState(false);

  const reportId = parsedReport?.report_id || parsedReport?.id || null;

  // Visitor generated this report, then signed up: attach it to the new account.
  useEffect(() => {
    if (!user || reportId || !parsedReport?.chart) return;
    claimLocalReport().then(merged => { if (merged?.report_id) setParsedReport(merged); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, reportId]);
  const loadedLang = parsedReport?.lang || "en";
  const needsBody = !!reportId && !parsedReport?.report;
  const needsLangSwap = !!reportId && !!parsedReport?.report && loadedLang !== lang;

  // The dashboard list is trimmed (no `report` body) and the site language can change
  // after the report was generated — refetch the full body in the active language.
  useEffect(() => {
    if (!reportId || (!needsBody && !needsLangSwap)) return;
    let alive = true;
    setFetching(true);
    axios.get(`${API}/kundli/report/${reportId}`, { params: { lang }, withCredentials: true, timeout: 180000 })
      .then(res => {
        if (!alive || !res.data?.report) return;
        const merged = { ...parsedReport, ...res.data };
        setParsedReport(merged);
        try { window.localStorage.setItem("astrofate_report", JSON.stringify(merged)); } catch { /* quota */ }
      })
      .catch(() => {})
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, lang, needsBody, needsLangSwap]);

  // Fetched alongside the report, never blocking it: the card slots in when ready.
  const [deep, setDeep] = useState({ answer: "", loading: true });
  useEffect(() => {
    if (!parsedReport?.chart && !reportId) { setDeep({ answer: "", loading: false }); return undefined; }
    let alive = true;
    setDeep({ answer: "", loading: true });
    const stop = setTimeout(() => { if (alive) setDeep(d => ({ ...d, loading: false })); }, 45000);
    axios.post(`${API}/kundli/first-read-chat`, {
      chart_json: parsedReport?.chart || undefined, report_id: reportId || undefined, lang,
    }, { withCredentials: true, timeout: 45000 })
      .then(r => { if (alive) setDeep({ answer: r.data?.answer || "", loading: false }); })
      .catch(() => { if (alive) setDeep({ answer: "", loading: false }); })
      .finally(() => clearTimeout(stop));
    return () => { alive = false; clearTimeout(stop); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, lang, !!parsedReport?.chart]);

  const sections = useMemo(() => {
    if (!parsedReport) return [];
    let rep = parsedReport.report || "";
    // Frontend safety net: if the upstream "Not available in JSON" string slips through,
    // substitute the actual name entered in the birth form.
    const userName = parsedReport.birth_details?.name || parsedReport.name;
    if (userName && rep) {
      rep = rep.replace(/(\*\*Name:\*\*)\s*Not available in JSON/gi, `$1 ${userName}`);
    }
    // Diagnostic log — open DevTools Console to verify what the frontend received.
    // Lets you compare preview vs production "report length" in one glance.
    if (typeof window !== "undefined" && rep) {
      console.info(
        `[AstroFate] Report received: ${rep.length} chars · has_dashboard=${rep.includes("## Report Dashboard")} · has_RAG=${rep.includes("RAG Blocks")}`
      );
    }
    let parsed = [];
    try {
      parsed = parseReportSections(rep);
    } catch (e) {
      console.error("Kundli report parse error:", e);
    }
    // ── BULLETPROOF FALLBACK ──────────────────────────────────────────
    // If the parser somehow returns zero sections (unexpected upstream
    // format, parse error, etc.), surface the entire raw markdown as a
    // single "Report Dashboard" tab so the customer never sees a blank
    // page or thinks the report failed to generate.
    if (!parsed.length && rep) {
      parsed = [{
        id: "report-dashboard",
        num: 0,
        label: "Report Dashboard",
        icon: Layers,
        color: "#c084fc",
        title: "Report Dashboard",
        markdown: rep,
      }];
    }
    return parsed;
  }, [parsedReport]);
  const [activeId, setActiveId] = useState(sections[0]?.id || null);

  if (!parsedReport) return <Navigate to="/kundli" replace />;

  // Body still loading (came from the dashboard list, or the language just changed)
  if (!parsedReport.report) {
    return (
      <div data-testid="kundli-report-loading" style={{ minHeight: "100vh", background: "#120021", paddingTop: "120px", textAlign: "center", padding: "120px 20px" }}>
        <div className="spinner" style={{ margin: "0 auto 18px" }} />
        <p style={{ color: "#FFD166", fontFamily: "Cinzel, serif" }}>
          {fetching ? "Loading your saved Kundli…" : "This report could not be loaded."}
        </p>
        {!fetching && (
          <button onClick={() => navigate("/kundli")} className="btn-gold" style={{ marginTop: "18px" }}>Generate a new Kundli</button>
        )}
      </div>
    );
  }

  const summary = parseSummaryFromReport(parsedReport.report);
  const birth = parsedReport.birth_details || {};
  const displayName = birth.name || parsedReport.name || "Your Birth Chart";

  const formatBirthLine = () => {
    if (!birth.day) return "";
    const period = birth.period || (birth.hour < 12 ? "AM" : "PM");
    const hour12 = birth.hour12 || ((birth.hour % 12) || 12);
    return `${birth.day}/${birth.month}/${birth.year} • ${String(hour12).padStart(2,"0")}:${String(birth.minute ?? 0).padStart(2,"0")} ${period} • ${birth.city || ""}`;
  };

  const activeSection = sections.find((s) => s.id === activeId) || sections[0];
  const colorGrad = (color, a = "22", b = "06") => `linear-gradient(135deg, ${color}${a}, ${color}${b})`;

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "80px", position: "relative" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(91,30,140,0.2), transparent)", top: 0, right: 0, borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(45,11,69,0.3), transparent)", bottom: 0, left: 0, borderRadius: "50%" }} />
      </div>

      <div className="kundli-container" style={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div className="animate-fadeInUp" style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
              <Star size={20} style={{ color: "#FFD166" }} fill="#FFD166" />
              <h1 className="font-cinzel kundli-h1" style={{ color: "#FFD166" }}>Your Kundli Report</h1>
            </div>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem" }}>
              Comprehensive Vedic Astrology Analysis · {sections.length} sections
            </p>
            {fetching && (
              <p data-testid="kundli-translating-note" style={{ color: "#FFD166", fontSize: "0.78rem", marginTop: "4px" }}>
                {lang === "hi" ? "रिपोर्ट का हिन्दी अनुवाद हो रहा है… (30-60 सेकंड)" : "Loading the English report…"}
              </p>
            )}
            {(parsedReport.report_id || parsedReport.id) && user && (
              <p data-testid="kundli-auto-emailed-note" style={{ color: "rgba(134,239,172,0.75)", fontSize: "0.78rem", marginTop: "4px" }}>
                ✓ We&apos;ve also emailed this report to you.
              </p>
            )}
          </div>
          <KundliPdfActions
            reportId={parsedReport.report_id || parsedReport.id || null}
            name={displayName}
            onFallback={() => downloadAsPdf({ name: displayName, birthLine: formatBirthLine(), summary, sections })}
          />
        </div>

        {/* Identity card */}
        <div className="glass-card animate-fadeInUp delay-100 kundli-identity" style={{ marginBottom: "22px", border: "1px solid rgba(255,209,102,0.15)", background: "linear-gradient(135deg, rgba(91,30,140,0.18), rgba(45,11,69,0.32))" }}>
          <div style={{ marginBottom: "14px" }}>
            <h2 className="font-cinzel" data-testid="kundli-name" style={{ fontSize: "clamp(1.25rem, 3.4vw, 1.6rem)", fontWeight: "700", color: "white", marginBottom: "2px" }}>
              {displayName}
            </h2>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.84rem" }}>{formatBirthLine()}</p>
          </div>
          <div className="kundli-summary-grid">
            {[
              { label: "Ascendant", value: summary.ascendant || "—", color: "#E2D8F0" },
              { label: "Sun Sign",  value: summary.sun_sign  || "—", color: "#FFD166" },
              { label: "Moon Sign", value: summary.moon_sign || "—", color: "#c084fc" },
              { label: "Nakshatra", value: summary.nakshatra || "—", color: "#86efac" },
              { label: "Mahadasha", value: summary.mahadasha || "—", color: "#67e8f9" },
            ].map((it) => (
              <div key={it.label} className="kundli-summary-cell" style={{ borderColor: `${it.color}33` }}>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", marginBottom: "4px", letterSpacing: "0.4px", textTransform: "uppercase" }}>{it.label}</div>
                <div style={{ color: it.color, fontWeight: "700", fontSize: "0.96rem" }}>{it.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Your first read — real value before any signup ask */}
        <FirstReadCard chart={parsedReport?.chart} reportId={parsedReport?.report_id || parsedReport?.id || reportId}
          name={parsedReport?.name || ""} reportMd={parsedReport?.report || ""} showScrollHint />

        {/* Arrives a few seconds later, on its own, with both next steps */}
        <NatureReadCard deep={deep} reportId={reportId} />

        {/* Explore the chart instead of reading at it — tappable Dasha chapters */}
        <LifeTimeline chart={parsedReport?.chart} reportId={parsedReport?.report_id || parsedReport?.id || reportId}
          name={displayName} birth={birth} summary={summary} />

        {/* Share the app, earn free questions */}
        {user && <InviteFriend compact />}

        {/* Visitors: save the report by creating a free account (one tap, comes back here) */}
        {user === null && (
          <div data-testid="report-signup-nudge" className="glass-card animate-fadeInUp delay-100" style={{ marginBottom: "18px", padding: "14px 16px", border: "1px solid rgba(255,209,102,0.35)", background: "linear-gradient(135deg, rgba(255,209,102,0.10), rgba(157,78,221,0.08))", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0, flex: "1 1 240px" }}>
              <div style={{ color: "#FFD166", fontWeight: 700, fontSize: "0.92rem" }}>✦ {t("report.saveTitle")}</div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", marginTop: "3px", lineHeight: 1.5 }}>{t("report.saveDesc")}</div>
            </div>
            <button data-testid="report-signup-btn" onClick={() => navigate(signupUrl("/kundli/report"))} className="btn-gold" style={{ padding: "10px 20px", fontSize: "0.86rem", flexShrink: 0 }}>
              {t("report.saveCta")}
            </button>
          </div>
        )}

        {/* Top CTAs — placed right after the chart highlights (the moment of peak
            interest) so both conversion paths are one tap away. Slightly smaller
            than the buttons at the end of the report. */}
        <div data-testid="kundli-top-ctas" className="kundli-top-ctas">
          <span className="kundli-top-ctas-label">Your full report is ready — go deeper:</span>
          <div className="kundli-top-ctas-row">
            <button data-testid="kundli-top-chat-btn" onClick={() => navigate("/chat")} className="btn-gold kundli-top-btn">
              <MessageCircle size={14} style={{ display: "inline", marginRight: "7px", verticalAlign: "-2px" }} />
              <span className="cta-full">Ask AI About This Chart</span><span className="cta-mini">Ask AI About Chart</span>
            </button>
            <button data-testid="kundli-top-consult-btn" onClick={() => navigate("/consultation")} className="btn-purple kundli-top-btn">
              <span className="cta-full">Talk to an Astrologer</span><span className="cta-mini">Talk to Astrologer</span>
            </button>
          </div>
        </div>

        {/* Pill section tabs — DYNAMIC, title-routed, 1-to-1 with report sections */}
        <div className="kundli-tabs" data-testid="kundli-tabs">
          {sections.map((s) => {
            const Icon = s.icon;
            const isActive = activeId === s.id;
            return (
              <button
                key={s.id}
                data-testid={`kundli-tab-${s.id}`}
                data-section-label={s.label}
                onClick={() => setActiveId(s.id)}
                className="kundli-tab"
                style={{
                  background: isActive ? colorGrad(s.color) : "rgba(255,255,255,0.04)",
                  borderColor: isActive ? `${s.color}55` : "rgba(255,255,255,0.06)",
                  color: isActive ? s.color : "rgba(255,255,255,0.6)",
                  fontWeight: isActive ? 700 : 500,
                }}
              >
                <Icon size={14} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Active section card */}
        {activeSection ? (
          <div
            key={activeSection.id}
            className="glass-card animate-fadeInUp kundli-section-card"
            data-testid={`kundli-section-${activeSection.id}`}
            data-section-label={activeSection.label}
            data-section-md-len={activeSection.markdown?.length || 0}
            style={{
              background: colorGrad(activeSection.color),
              border: `1px solid ${activeSection.color}33`,
              boxShadow: `0 4px 32px ${activeSection.color}11`,
            }}
          >
            <div className="kundli-section-header" style={{ borderBottom: `1px solid ${activeSection.color}22` }}>
              <activeSection.icon size={18} style={{ color: activeSection.color }} />
              <h2 className="font-cinzel" style={{ color: activeSection.color, fontSize: "1.05rem", letterSpacing: "0.4px" }}>
                {activeSection.title || activeSection.label}
              </h2>
            </div>
            <div className="kundli-report" style={{ padding: "18px 18px 8px" }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{activeSection.markdown}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="glass-card" style={{ padding: "30px", textAlign: "center", color: "rgba(255,255,255,0.55)" }}>
            <Activity size={20} style={{ color: "#FFD166", marginBottom: "8px" }} />
            <p>No sections could be parsed from this report. Please regenerate.</p>
          </div>
        )}

        {/* Stage-aware next-step offer */}
        <div style={{ marginTop: "22px" }}>
          <SmartOfferBanner />
        </div>

        {/* CTA */}
        <div className="kundli-cta">
          <button data-testid="kundli-chat-btn" onClick={() => navigate("/chat")} className="btn-gold">
            <MessageCircle size={15} style={{ display: "inline", marginRight: "8px" }} />
            Ask AI About This Chart
          </button>
          <button data-testid="kundli-consult-btn" onClick={() => navigate("/consultation")} className="btn-purple">
            Book Expert Consultation
          </button>
          <button data-testid="kundli-new-btn" onClick={() => navigate("/kundli")} className="btn-ghost">
            ✦ New Kundli
          </button>
        </div>
      </div>
    </div>
  );
}
